import { leadAttributionContext, type LeadAttributionContext } from "@/lib/leadAttribution";
import { discoverySourceLabel, normalizeDiscoverySource, type DiscoverySource } from "@/lib/discoverySource";

const FORM_SUBMIT_URL = "https://formsubmit.co/ajax/chleb@chleb.ai";
const EDGE_TIMEOUT_MS = 8_000;
const MAX_RESPONSE_BYTES = 64 * 1024;

export type WebsiteLead =
  | {
      kind: "contact";
      name: string;
      business: string;
      email: string;
      phone?: string;
      message: string;
      selfReportedDiscoverySource?: DiscoverySource;
      honeypot?: string;
    }
  | {
      kind: "calculator";
      email: string;
      calls: number;
      jobValue: number;
      missedRate: number;
      closeRate: number;
      annualRecovery: number;
      honeypot?: string;
    };

/**
 * Confirms provider acceptance only. Inbox arrival and CRM creation remain
 * unknown unless the owned intake explicitly confirms the latter.
 */
export interface LeadSubmissionResult {
  requestId: string;
  acceptedBy: "owned-intake" | "formsubmit";
  emailArrival: "unknown";
  crmCreation: "confirmed" | "unknown";
  possibleDuplicate: boolean;
  /**
   * After an owned-intake receipt, the same email still goes to the inbox as
   * the notice someone reads. "failed" never fails the submission: the lead is
   * already stored. Absent when email was the delivery path itself.
   */
  emailNotice?: "accepted" | "failed";
}

interface SubmitOptions {
  fetcher?: typeof fetch;
  edgeEndpoint?: string | null;
  timeoutMs?: number;
  requestId?: string;
  context?: LeadAttributionContext;
}

class IntakeRejectedError extends Error {}

function configuredEdgeEndpoint() {
  const explicit = import.meta.env.VITE_LEAD_ENDPOINT?.trim();
  if (explicit) return explicit;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim().replace(/\/$/, "");
  return supabaseUrl ? `${supabaseUrl}/functions/v1/submit-lead` : null;
}

export function createLeadRequestId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
}

function abortReason(signal: AbortSignal) {
  return signal.reason instanceof Error ? signal.reason : new DOMException("The request timed out", "AbortError");
}

async function readChunk(reader: ReadableStreamDefaultReader<Uint8Array>, signal: AbortSignal) {
  if (signal.aborted) {
    void reader.cancel().catch(() => undefined);
    throw abortReason(signal);
  }

  return await new Promise<ReadableStreamReadResult<Uint8Array>>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", onAbort);
      callback();
    };
    const onAbort = () => {
      void reader.cancel().catch(() => undefined);
      finish(() => reject(abortReason(signal)));
    };

    signal.addEventListener("abort", onAbort, { once: true });
    reader.read().then(
      (result) => finish(() => resolve(result)),
      (error) => finish(() => reject(error)),
    );
  });
}

async function cancelBody(response: Response) {
  if (!response.body || response.bodyUsed) return;
  try {
    void response.body.cancel().catch(() => undefined);
  } catch {
    // Cancellation is best-effort after deciding not to consume a body.
  }
}

async function readBoundedJson(response: Response, signal: AbortSignal): Promise<unknown> {
  if (!response.body) return null;

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const { done, value } = await readChunk(reader, signal);
      if (done) break;
      if (!value) continue;
      if (total + value.byteLength > MAX_RESPONSE_BYTES) {
        void reader.cancel("Response body too large").catch(() => undefined);
        throw new Error(`Lead provider response exceeded ${MAX_RESPONSE_BYTES} bytes`);
      }
      chunks.push(value);
      total += value.byteLength;
    }
  } finally {
    reader.releaseLock();
  }

  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  const text = new TextDecoder().decode(bytes).trim();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error("Lead provider returned malformed JSON");
  }
}

async function postWithinDeadline<T>(
  fetcher: typeof fetch,
  url: string,
  body: unknown,
  timeoutMs: number,
  inspect: (response: Response, signal: AbortSignal) => Promise<T>,
  headers: Record<string, string> = {},
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetcher(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", ...headers },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return await inspect(response, controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

function fallbackBody(lead: WebsiteLead, id: string, context: LeadAttributionContext, storedNotice = false) {
  const noticeTag = storedNotice ? " (stored in owned intake)" : "";
  const attribution = {
    "Entry page": context.entryPagePath,
    "Current page": context.pagePath,
    "Referrer host": context.referrerHost,
    "UTM source": context.utm.source,
    "UTM medium": context.utm.medium,
    "UTM campaign": context.utm.campaign,
  };

  if (lead.kind === "contact") {
    return {
      name: lead.name,
      "Business Name": lead.business,
      email: lead.email,
      phone: lead.phone ?? "",
      message: lead.message,
      "Discovery source (self-reported)": discoverySourceLabel(lead.selfReportedDiscoverySource),
      "Request ID": id,
      ...attribution,
      "Follow-up required": "Review inquiry and scope a proposal; no booking or service has been confirmed",
      _replyto: lead.email,
      _subject: `New Contact from Chleb AI Website${noticeTag}`,
      _captcha: "true",
      _honeypot: lead.honeypot ?? "",
    };
  }

  return {
    email: lead.email,
    "Monthly calls": lead.calls,
    "Avg job value": lead.jobValue,
    "Missed call rate": `${lead.missedRate}%`,
    "Close rate": `${lead.closeRate}%`,
    "Estimated annual recovery": lead.annualRecovery,
    "Request ID": id,
    ...attribution,
    "Follow-up required": "Review the requested estimate; no automatic result email has been sent",
    _replyto: lead.email,
    _subject: `Revenue Recovery Report request${noticeTag}`,
    _captcha: "true",
    _honeypot: lead.honeypot ?? "",
  };
}

async function deliverFallback(
  fetcher: typeof fetch,
  lead: WebsiteLead,
  id: string,
  context: LeadAttributionContext,
  timeoutMs: number,
  storedNotice = false,
) {
  const payload = await postWithinDeadline(
    fetcher,
    FORM_SUBMIT_URL,
    fallbackBody(lead, id, context, storedNotice),
    timeoutMs,
    async (response, signal) => {
      if (!response.ok) {
        await cancelBody(response);
        throw new Error("Lead delivery failed");
      }
      try {
        return await readBoundedJson(response, signal);
      } catch (error) {
        // A body-size guard or an abort has its own specific message worth
        // keeping; a parse failure collapses into the same reported failure
        // as every other unacknowledged fallback below.
        if (error instanceof Error && error.message === "Lead provider returned malformed JSON") {
          throw new Error("Lead delivery failed");
        }
        throw error;
      }
    },
  );

  // Accept only an explicit affirmative acknowledgement. Some FormSubmit
  // integrations serialize booleans as strings; missing/empty data never passes.
  const acknowledgement = payload && typeof payload === "object"
    ? (payload as Record<string, unknown>).success : undefined;
  if (acknowledgement !== true && acknowledgement !== "true") {
    throw new Error("Lead delivery failed");
  }
}

function canFallbackForStatus(status: number) {
  // A 403 can be an explicit origin/policy refusal, not an unavailable service.
  return status === 401 || status === 404 || status >= 500;
}

// leadAttributionContext() sanitizes pagePath down to a bare path (no query
// string or hash). The website-care CTA links to /?service=website-care#contact
// so a human reviewing the inquiry can tell it came from that scoped ask.
// Preserve only that known marker and the contact anchor; arbitrary fragments
// may contain private values and must not bypass the attribution sanitizer.
function withCurrentPageDetail(context: LeadAttributionContext): LeadAttributionContext {
  if (typeof window === "undefined") return context;
  const params = new URLSearchParams(window.location.search);
  const serviceMarker = params.get("service") === "website-care" ? "?service=website-care" : "";
  const contactAnchor = window.location.hash === "#contact" ? "#contact" : "";
  return { ...context, pagePath: `${context.pagePath}${serviceMarker}${contactAnchor}` };
}

export async function submitWebsiteLead(
  lead: WebsiteLead,
  options: SubmitOptions = {},
): Promise<LeadSubmissionResult> {
  // Keep the optional answer bounded in both delivery paths, even if a caller
  // bypasses the typed form. It never replaces observed attribution.
  if (lead.kind === "contact") {
    lead = { ...lead, selfReportedDiscoverySource: normalizeDiscoverySource(lead.selfReportedDiscoverySource) };
  }
  const fetcher = options.fetcher ?? fetch;
  const edgeEndpoint = options.edgeEndpoint === undefined ? configuredEdgeEndpoint() : options.edgeEndpoint;
  const timeoutMs = options.timeoutMs ?? EDGE_TIMEOUT_MS;
  const id = options.requestId ?? createLeadRequestId();
  const context = options.context ?? withCurrentPageDetail(leadAttributionContext());
  let possibleDuplicate = false;

  if (edgeEndpoint) {
    try {
      const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();
      const accepted = await postWithinDeadline(
        fetcher,
        edgeEndpoint,
        { ...lead, requestId: id, ...context },
        timeoutMs,
        async (response, signal) => {
          if (canFallbackForStatus(response.status)) {
            // A gateway/server failure can happen after persistence. Only the
            // unavailable/auth endpoint statuses avoid that ambiguity here.
            if (response.status >= 500) possibleDuplicate = true;
            await cancelBody(response);
            return null;
          }

          let payload: unknown;
          try {
            payload = await readBoundedJson(response, signal);
          } catch (error) {
            if (response.status >= 400 && response.status < 500) {
              throw new IntakeRejectedError("Lead submission was rejected");
            }
            throw error;
          }
          const record = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
          // Require the intake to echo the same request ID back before trusting
          // "stored": a generic ok/stored ack that isn't tied to this specific
          // submission cannot prove this retry (or a stale response) was saved.
          if (response.ok && record?.ok === true && record.stored === true && record.requestId === id) {
            return {
              requestId: id,
              acceptedBy: "owned-intake" as const,
              emailArrival: "unknown" as const,
              crmCreation: record.crmSynced === true ? ("confirmed" as const) : ("unknown" as const),
              possibleDuplicate: false,
            };
          }
          // An explicit rejection (the intake validated the request and said no)
          // is authoritative and must not be silently retried over email. A 2xx
          // that merely fails to prove storage is ambiguous, not a rejection;
          // fall back the same way an unavailable intake would.
          const explicitlyRejected = (response.status >= 400 && response.status < 500) ||
            record?.ok === false || typeof record?.error === "string";
          if (!explicitlyRejected) {
            possibleDuplicate = true;
            return null;
          }
          throw new IntakeRejectedError(
            typeof record?.error === "string" ? record.error : "Lead submission was rejected",
          );
        },
        publishableKey ? { apikey: publishableKey } : {},
      );
      if (accepted) {
        // The table is the record; the inbox is where people notice a lead.
        // Nothing else alerts anyone to a new row, so keep sending the email.
        try {
          await deliverFallback(fetcher, lead, id, context, timeoutMs, true);
          return { ...accepted, emailNotice: "accepted" as const };
        } catch {
          return { ...accepted, emailNotice: "failed" as const };
        }
      }
    } catch (error) {
      if (error instanceof IntakeRejectedError) throw error;
      // Once a request was attempted, a network/body failure cannot prove the
      // owned intake did not accept it. The same request ID makes reconciliation possible.
      possibleDuplicate = true;
    }
  }

  await deliverFallback(fetcher, lead, id, context, timeoutMs);
  return {
    requestId: id,
    acceptedBy: "formsubmit",
    emailArrival: "unknown",
    crmCreation: "unknown",
    possibleDuplicate,
  };
}

// Keep an unchanged form's key across uncertain retries, without storing PII in browser storage.
export function createWebsiteLeadSubmitter(options: SubmitOptions = {}) {
  let previousPayload = "";
  let id = "";
  let context: LeadAttributionContext | undefined;
  return (lead: WebsiteLead) => {
    const payload = JSON.stringify(lead);
    if (payload !== previousPayload) {
      previousPayload = payload;
      id = createLeadRequestId();
      const observed = options.context ?? withCurrentPageDetail(leadAttributionContext());
      context = { ...observed, utm: { ...observed.utm } };
    }
    return submitWebsiteLead(lead, { ...options, requestId: id, context });
  };
}
